import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import WifiTetheringIcon from "@material-ui/icons/WifiTethering";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import MoveToInboxOutlinedIcon from "@material-ui/icons/MoveToInboxOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import ConfirmationModal from "../../components/ConfirmationModal";

/**
 * Contas de envio de e-mail.
 *
 * A conta que o fornecedor deixa pronta na implantação não aparece aqui — ela
 * envia, mas é gerenciada fora da tela do cliente. Quando ele cadastra a
 * própria conta e marca como padrão, os e-mails passam a sair no nome dele.
 */
const useStyles = makeStyles((theme) => ({
  secao: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  linha: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  erro: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    marginTop: theme.spacing(1),
  },
}));

const VAZIO = {
  name: "",
  fromEmail: "",
  fromName: "",
  replyTo: "",
  smtpHost: "",
  smtpPort: 465,
  smtpUser: "",
  smtpPassword: "",
  encryption: "ssl",
  maxPerHour: 100,
  maxPerDay: 1000,
  isActive: true,
  isDefault: false,
  imapHost: "",
  imapPort: 993,
  imapUser: "",
  imapPassword: "",
  imapEncryption: "ssl",
  inboundActive: false,
};

const EmailAccounts = () => {
  const classes = useStyles();

  const [contas, setContas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [editor, setEditor] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(null);
  const [testandoImap, setTestandoImap] = useState(null);
  const [confirmar, setConfirmar] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/email-accounts");
      setContas(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const mudar = (campo, valor) =>
    setEditor((atual) => ({ ...atual, [campo]: valor }));

  // A porta indica o modo de conexão: 465 fala TLS desde o primeiro byte,
  // 587 abre em claro e sobe por STARTTLS. Trocar os dois é o erro mais comum
  // de SMTP, e aparece como espera longa seguida de falha.
  const mudarPorta = (valor) => {
    const porta = Number(valor);
    setEditor((atual) => ({
      ...atual,
      smtpPort: valor,
      encryption: porta === 465 ? "ssl" : porta === 587 ? "tls" : atual.encryption,
    }));
  };

  // Mesma lógica da SMTP: a porta indica o modo de conexão IMAP.
  const mudarPortaImap = (valor) => {
    const porta = Number(valor);
    setEditor((atual) => ({
      ...atual,
      imapPort: valor,
      imapEncryption: porta === 993 ? "ssl" : porta === 143 ? "tls" : atual.imapEncryption,
    }));
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const corpo = {
        ...editor,
        smtpPort: Number(editor.smtpPort) || 465,
        maxPerHour: Number(editor.maxPerHour) || 0,
        maxPerDay: Number(editor.maxPerDay) || 0,
        replyTo: editor.replyTo || null,
        fromName: editor.fromName || null,
        imapPort: Number(editor.imapPort) || 993,
        imapHost: editor.imapHost || null,
        imapUser: editor.imapUser || null,
      };
      // Senha em branco na edição mantém a atual — permite corrigir host ou
      // porta sem redigitar a credencial.
      if (editor.id && !editor.smtpPassword) delete corpo.smtpPassword;
      if (editor.id && !editor.imapPassword) delete corpo.imapPassword;

      if (editor.id) await api.put(`/email-accounts/${editor.id}`, corpo);
      else await api.post("/email-accounts", corpo);

      toast.success("Conta salva.");
      setEditor(null);
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const testar = async (conta) => {
    setTestando(conta.id);
    try {
      const { data } = await api.post(`/email-accounts/${conta.id}/test`);
      toast.success(data.message || "Conexão estabelecida.");
      carregar();
    } catch (err) {
      const msg = err?.response?.data?.message;
      if (msg) toast.error(msg);
      else toastError(err);
      carregar();
    } finally {
      setTestando(null);
    }
  };

  const testarImap = async (conta) => {
    setTestandoImap(conta.id);
    try {
      const { data } = await api.post(`/email-accounts/${conta.id}/test-imap`);
      toast.success(data.message || "Conexão IMAP estabelecida.");
      carregar();
    } catch (err) {
      const msg = err?.response?.data?.message;
      if (msg) toast.error(msg);
      else toastError(err);
      carregar();
    } finally {
      setTestandoImap(null);
    }
  };

  const excluir = async () => {
    try {
      await api.delete(`/email-accounts/${confirmar.id}`);
      toast.success("Conta removida.");
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmar(null);
    }
  };

  const colunas = useMemo(
    () => [
      {
        key: "name",
        label: "Conta",
        render: (row) => (
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {row.fromName ? `${row.fromName} <${row.fromEmail}>` : row.fromEmail}
            </Typography>
          </Box>
        ),
      },
      {
        key: "smtp",
        label: "Servidor",
        render: (row) => (
          <Typography variant="caption" color="textSecondary">
            {row.smtpHost}:{row.smtpPort} · {String(row.encryption).toUpperCase()}
          </Typography>
        ),
      },
      {
        key: "limites",
        label: "Limites",
        render: (row) => (
          <Typography variant="caption" color="textSecondary">
            {row.maxPerHour}/h · {row.maxPerDay}/dia
          </Typography>
        ),
      },
      {
        key: "situacao",
        label: "Situação",
        render: (row) => (
          <Box display="flex" style={{ gap: 4 }} flexWrap="wrap">
            {row.isDefault && <Chip size="small" color="primary" label="Padrão" />}
            <Chip
              size="small"
              label={row.isActive ? "Ativa" : "Inativa"}
              color={row.isActive ? "default" : "secondary"}
            />
            {row.inboundActive && (
              <Chip size="small" icon={<MoveToInboxOutlinedIcon />} label="Recebe e-mail" />
            )}
            {row.lastError && (
              <Tooltip title={row.lastError}>
                <Chip size="small" label="Com erro (envio)" style={{ color: "#DC2626" }} />
              </Tooltip>
            )}
            {row.lastInboundError && (
              <Tooltip title={row.lastInboundError}>
                <Chip size="small" label="Com erro (recebimento)" style={{ color: "#DC2626" }} />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        key: "acoes",
        label: "",
        type: "actions",
        render: (row) => (
          <Box display="flex" justifyContent="flex-end">
            <Tooltip title="Testar conexão SMTP (envio)">
              <span>
                <IconButton
                  size="small"
                  onClick={() => testar(row)}
                  disabled={testando === row.id}
                >
                  <WifiTetheringIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {row.imapHost && (
              <Tooltip title="Testar conexão IMAP (recebimento)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => testarImap(row)}
                    disabled={testandoImap === row.id}
                  >
                    <MoveToInboxOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Editar">
              <IconButton
                size="small"
                onClick={() =>
                  setEditor({ ...VAZIO, ...row, smtpPassword: "", imapPassword: "" })
                }
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={() => setConfirmar(row)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testando, testandoImap]
  );

  return (
    <MainContainer>
      <PageHeader
        title="Contas de envio"
        count={contas.length}
        subtitle="De qual endereço saem os e-mails do CRM — notificações, propostas e cobranças."
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setEditor({ ...VAZIO })}
          >
            Nova conta
          </Button>
        }
      />

      <DataTable
        columns={colunas}
        rows={contas}
        loading={carregando}
        getRowKey={(row) => row.id}
        emptyIcon={<MailOutlineIcon />}
        emptyTitle="Nenhuma conta própria cadastrada"
        emptyDescription="Os e-mails saem por uma conta configurada na implantação. Cadastre a sua para que passem a sair no seu endereço."
        emptyAction={
          <Button
            variant="contained"
            color="primary"
            onClick={() => setEditor({ ...VAZIO })}
          >
            Cadastrar conta
          </Button>
        }
      />

      <Dialog
        open={!!editor}
        onClose={() => setEditor(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {editor?.id ? "Editar conta de envio" : "Nova conta de envio"}
        </DialogTitle>
        <DialogContent dividers>
          {editor && (
            <>
              {editor.lastError && (
                <div className={classes.erro}>
                  <Typography variant="caption">
                    Último erro: {editor.lastError}
                  </Typography>
                </div>
              )}

              <TextField
                fullWidth
                variant="outlined"
                label="Nome da conta"
                helperText="Só para você identificar na lista."
                value={editor.name}
                onChange={(e) => mudar("name", e.target.value)}
              />

              <div className={classes.linha}>
                <TextField
                  variant="outlined"
                  label="E-mail remetente"
                  style={{ flex: "2 1 260px" }}
                  value={editor.fromEmail}
                  onChange={(e) => mudar("fromEmail", e.target.value)}
                />
                <TextField
                  variant="outlined"
                  label="Nome exibido"
                  style={{ flex: "1 1 180px" }}
                  value={editor.fromName || ""}
                  onChange={(e) => mudar("fromName", e.target.value)}
                />
                <TextField
                  variant="outlined"
                  label="Responder para (opcional)"
                  style={{ flex: "2 1 240px" }}
                  helperText="Para onde vão as respostas, se for diferente do remetente."
                  value={editor.replyTo || ""}
                  onChange={(e) => mudar("replyTo", e.target.value)}
                />
              </div>

              <div className={classes.secao}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Servidor SMTP
                </Typography>
                <div className={classes.linha}>
                  <TextField
                    variant="outlined"
                    label="Host"
                    style={{ flex: "2 1 240px" }}
                    placeholder="smtp.seuprovedor.com"
                    value={editor.smtpHost || ""}
                    onChange={(e) => mudar("smtpHost", e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    label="Porta"
                    type="number"
                    style={{ flex: "0 1 120px" }}
                    value={editor.smtpPort}
                    onChange={(e) => mudarPorta(e.target.value)}
                  />
                  <TextField
                    select
                    variant="outlined"
                    label="Criptografia"
                    style={{ flex: "0 1 160px" }}
                    helperText="465 usa SSL · 587 usa TLS"
                    value={editor.encryption}
                    onChange={(e) => mudar("encryption", e.target.value)}
                  >
                    <MenuItem value="ssl">SSL</MenuItem>
                    <MenuItem value="tls">TLS (STARTTLS)</MenuItem>
                    <MenuItem value="none">Sem criptografia</MenuItem>
                  </TextField>
                </div>
                <div className={classes.linha}>
                  <TextField
                    variant="outlined"
                    label="Usuário"
                    style={{ flex: "2 1 240px" }}
                    value={editor.smtpUser || ""}
                    onChange={(e) => mudar("smtpUser", e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    type="password"
                    label="Senha"
                    style={{ flex: "2 1 240px" }}
                    autoComplete="new-password"
                    helperText={
                      editor.id
                        ? "Em branco mantém a senha atual."
                        : "Fica cifrada e nunca volta para a tela."
                    }
                    value={editor.smtpPassword || ""}
                    onChange={(e) => mudar("smtpPassword", e.target.value)}
                  />
                </div>
              </div>

              <div className={classes.secao}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    Recebimento (IMAP)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!editor.inboundActive}
                        onChange={(e) => mudar("inboundActive", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="E-mails recebidos viram ticket"
                  />
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Opcional. Sem isto, esta conta só envia — ninguém vê o que
                  chega nela dentro do CRM.
                </Typography>
                <div className={classes.linha}>
                  <TextField
                    variant="outlined"
                    label="Host"
                    style={{ flex: "2 1 240px" }}
                    placeholder="imap.seuprovedor.com"
                    value={editor.imapHost || ""}
                    onChange={(e) => mudar("imapHost", e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    label="Porta"
                    type="number"
                    style={{ flex: "0 1 120px" }}
                    value={editor.imapPort}
                    onChange={(e) => mudarPortaImap(e.target.value)}
                  />
                  <TextField
                    select
                    variant="outlined"
                    label="Criptografia"
                    style={{ flex: "0 1 160px" }}
                    helperText="993 usa SSL · 143 usa TLS"
                    value={editor.imapEncryption}
                    onChange={(e) => mudar("imapEncryption", e.target.value)}
                  >
                    <MenuItem value="ssl">SSL</MenuItem>
                    <MenuItem value="tls">TLS (STARTTLS)</MenuItem>
                    <MenuItem value="none">Sem criptografia</MenuItem>
                  </TextField>
                </div>
                <div className={classes.linha}>
                  <TextField
                    variant="outlined"
                    label="Usuário"
                    style={{ flex: "2 1 240px" }}
                    value={editor.imapUser || ""}
                    onChange={(e) => mudar("imapUser", e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    type="password"
                    label="Senha"
                    style={{ flex: "2 1 240px" }}
                    autoComplete="new-password"
                    helperText={
                      editor.id
                        ? "Em branco mantém a senha atual."
                        : "Fica cifrada e nunca volta para a tela."
                    }
                    value={editor.imapPassword || ""}
                    onChange={(e) => mudar("imapPassword", e.target.value)}
                  />
                </div>
              </div>

              <div className={classes.secao}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Limites de envio
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Provedor bloqueia remetente que dispara demais — e o bloqueio
                  derruba o envio de todo o sistema, não só de uma mensagem.
                </Typography>
                <div className={classes.linha}>
                  <TextField
                    variant="outlined"
                    label="Máximo por hora"
                    type="number"
                    style={{ flex: "0 1 180px" }}
                    value={editor.maxPerHour}
                    onChange={(e) => mudar("maxPerHour", e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    label="Máximo por dia"
                    type="number"
                    style={{ flex: "0 1 180px" }}
                    value={editor.maxPerDay}
                    onChange={(e) => mudar("maxPerDay", e.target.value)}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!editor.isActive}
                        onChange={(e) => mudar("isActive", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Ativa"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!editor.isDefault}
                        onChange={(e) => mudar("isDefault", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Usar como padrão"
                  />
                </div>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={salvar}
            disabled={salvando || !editor?.name || !editor?.fromEmail}
          >
            {salvando ? "Salvando…" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir conta de envio"
        open={!!confirmar}
        onClose={() => setConfirmar(null)}
        onConfirm={excluir}
      >
        Os e-mails passarão a sair pela próxima conta ativa. O histórico de
        envios permanece.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default EmailAccounts;
